import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createComment,
  deleteComment,
  editComment,
  fetchComments,
  resolveRequest,
  type Comment,
  type Me,
} from '../api';
import { Badge, Empty, ErrorBox, Loading, RelativeTime, Section } from '../components/ui';

/**
 * Conversación de un proyecto: comentarios y peticiones de función.
 *
 * Es lo que el portal no tenía y por lo que no se podía trabajar en equipo con él. Una
 * petición que solo vive en un chat fuera del sistema se pierde; aquí queda colgada del
 * proyecto, con fecha y firma, y un administrador puede convertirla en tarea o cerrarla
 * —pero no cerrarla en silencio: la interfaz exige un motivo, igual que la API.
 *
 * Un observador ve la conversación entera y no puede escribir. Se le dice por qué en vez
 * de esconderle el formulario: un botón que no aparece parece un fallo de la aplicación.
 */
export function ConversationTab({ projectId, me }: { projectId: string; me: Me }): JSX.Element {
  const client = useQueryClient();
  const [texto, setTexto] = useState('');
  const [clase, setClase] = useState<'comment' | 'feature_request'>('comment');
  const [editando, setEditando] = useState<string | null>(null);
  const [borrador, setBorrador] = useState('');
  const [motivos, setMotivos] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['comments', 'project', projectId],
    queryFn: () => fetchComments('project', projectId),
  });

  const refrescar = (): void => {
    void client.invalidateQueries({ queryKey: ['comments', 'project', projectId] });
  };

  const crear = useMutation({
    mutationFn: () =>
      createComment({ entityType: 'project', entityId: projectId, kind: clase, body: texto }),
    onSuccess: () => {
      setTexto('');
      refrescar();
    },
  });
  const editar = useMutation({
    mutationFn: (input: { id: string; body: string }) => editComment(input.id, input.body),
    onSuccess: () => {
      setEditando(null);
      refrescar();
    },
  });
  const borrar = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: refrescar,
  });
  const resolver = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      resolveRequest(input.id, { reason: input.reason }),
    onSuccess: refrescar,
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const puedeEscribir = me.role === 'admin' || me.role === 'member';
  const comentarios = query.data.comments;
  const peticionesAbiertas = comentarios.filter(
    (c) => c.kind === 'feature_request' && c.resolved_at === null,
  ).length;

  return (
    <div className="space-y-6">
      <Section
        title="Conversación"
        subtitle={
          peticionesAbiertas > 0
            ? `${peticionesAbiertas} petición(es) de función sin responder`
            : 'Comentarios y peticiones de función de este proyecto'
        }
      >
        {comentarios.length === 0 ? (
          <Empty>
            Todavía no hay nada escrito. Las peticiones que se anoten aquí quedan con fecha y firma,
            en vez de perderse en un chat.
          </Empty>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {comentarios.map((c) => (
              <li key={c.id} className="py-4">
                <Mensaje
                  comentario={c}
                  me={me}
                  editando={editando === c.id}
                  borrador={borrador}
                  motivo={motivos[c.id] ?? ''}
                  onMotivo={(v) => setMotivos((m) => ({ ...m, [c.id]: v }))}
                  onEmpezarEdicion={() => {
                    setEditando(c.id);
                    setBorrador(c.body);
                  }}
                  onCambiarBorrador={setBorrador}
                  onCancelarEdicion={() => setEditando(null)}
                  onGuardar={() => editar.mutate({ id: c.id, body: borrador })}
                  onBorrar={() => borrar.mutate(c.id)}
                  onResolver={() => resolver.mutate({ id: c.id, reason: motivos[c.id] ?? '' })}
                  ocupado={editar.isPending || borrar.isPending || resolver.isPending}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Escribir">
        {!puedeEscribir ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Tu cuenta es de solo lectura. Puedes leer toda la conversación; para escribir, pide a un
            administrador el papel de colaborador.
          </p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (texto.trim()) crear.mutate();
            }}
          >
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['comment', 'Comentario'],
                  ['feature_request', 'Pedir una función'],
                ] as const
              ).map(([valor, etiqueta]) => (
                <button
                  key={valor}
                  type="button"
                  onClick={() => setClase(valor)}
                  className={
                    'rounded px-3 py-1 text-sm ' +
                    (clase === valor
                      ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300')
                  }
                >
                  {etiqueta}
                </button>
              ))}
            </div>
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              rows={4}
              maxLength={8000}
              placeholder={
                clase === 'feature_request'
                  ? 'Qué falta y para qué sirve. Cuanto más concreto, antes se puede convertir en tarea.'
                  : 'Escribe un comentario.'
              }
              className="w-full rounded border border-slate-300 bg-white p-2 text-sm dark:border-slate-600 dark:bg-slate-900"
            />
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={!texto.trim() || crear.isPending}
                className="rounded bg-slate-800 px-4 py-2 text-sm text-white disabled:opacity-40 dark:bg-slate-200 dark:text-slate-900"
              >
                {crear.isPending
                  ? 'Guardando…'
                  : clase === 'feature_request'
                    ? 'Enviar petición'
                    : 'Comentar'}
              </button>
              {crear.isError && <ErrorBox error={crear.error} />}
            </div>
          </form>
        )}
      </Section>
    </div>
  );
}

function Mensaje({
  comentario: c,
  me,
  editando,
  borrador,
  motivo,
  onMotivo,
  onEmpezarEdicion,
  onCambiarBorrador,
  onCancelarEdicion,
  onGuardar,
  onBorrar,
  onResolver,
  ocupado,
}: {
  comentario: Comment;
  me: Me;
  editando: boolean;
  borrador: string;
  motivo: string;
  onMotivo: (v: string) => void;
  onEmpezarEdicion: () => void;
  onCambiarBorrador: (v: string) => void;
  onCancelarEdicion: () => void;
  onGuardar: () => void;
  onBorrar: () => void;
  onResolver: () => void;
  ocupado: boolean;
}): JSX.Element {
  const esAutor = c.author_login.toLowerCase() === me.login.toLowerCase();
  const esPeticion = c.kind === 'feature_request';
  const abierta = esPeticion && c.resolved_at === null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{c.author_name ?? c.author_login}</span>
        <span className="text-slate-400">@{c.author_login}</span>
        <RelativeTime iso={c.created_at} />
        {c.edited_at && <span className="text-xs text-slate-400">(editado)</span>}
        {esPeticion && (
          <Badge tone={abierta ? 'warn' : 'good'}>
            {abierta ? 'Petición abierta' : 'Petición respondida'}
          </Badge>
        )}
      </div>

      {editando ? (
        <div className="space-y-2">
          <textarea
            value={borrador}
            onChange={(e) => onCambiarBorrador(e.target.value)}
            rows={4}
            className="w-full rounded border border-slate-300 bg-white p-2 text-sm dark:border-slate-600 dark:bg-slate-900"
          />
          <div className="flex gap-2">
            <button
              onClick={onGuardar}
              disabled={!borrador.trim() || ocupado}
              className="rounded bg-slate-800 px-3 py-1 text-sm text-white disabled:opacity-40 dark:bg-slate-200 dark:text-slate-900"
            >
              Guardar
            </button>
            <button
              onClick={onCancelarEdicion}
              className="rounded px-3 py-1 text-sm text-slate-500"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{c.body}</p>
      )}

      {c.work_item_id && (
        <p className="text-xs text-slate-500">
          Convertida en tarea: {c.work_item_title ?? `#${c.work_item_id}`}
        </p>
      )}
      {c.resolved_at && !c.work_item_id && (
        <p className="text-xs text-slate-500">Respondida por @{c.resolved_by}</p>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs">
        {esAutor && !editando && (
          <button onClick={onEmpezarEdicion} className="text-slate-500 hover:underline">
            Editar
          </button>
        )}
        {(esAutor || me.role === 'admin') && (
          <button onClick={onBorrar} disabled={ocupado} className="text-slate-500 hover:underline">
            Borrar
          </button>
        )}
      </div>

      {abierta && me.role === 'admin' && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded bg-slate-50 p-2 dark:bg-slate-800/60">
          <input
            value={motivo}
            onChange={(e) => onMotivo(e.target.value)}
            placeholder="Responde por qué se cierra (obligatorio)"
            className="min-w-[16rem] flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
          />
          <button
            onClick={onResolver}
            disabled={!motivo.trim() || ocupado}
            className="rounded bg-slate-800 px-3 py-1 text-sm text-white disabled:opacity-40 dark:bg-slate-200 dark:text-slate-900"
          >
            Cerrar petición
          </button>
        </div>
      )}
    </div>
  );
}
